import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { secureStorage } from '../utils/secureStorage';
import { BACKEND_URL } from '../utils/backendUrl';


/**
 * EMZI NEXUS V6 - WhatsApp Broadcast Mode Helper (ToS-Safe)
 * User-assisted broadcast workflow - no automation
 * Mobile-first design with ≥44px tap targets
 */
const BroadcastModeHelper = ({ 
  messageTemplate, 
  packIds, 
  recipients, 
  announcementId,
  onComplete 
}) => {
  const [activeTab, setActiveTab] = useState('compose'); // 'compose', 'send', 'tracking'
  const [generatedMessage, setGeneratedMessage] = useState('');
  const [deepLinks, setDeepLinks] = useState({});
  const [showHelper, setShowHelper] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [recipientsSaved, setRecipientsSaved] = useState(false);

  useEffect(() => {
    if (recipients && recipients.length > 0 && messageTemplate) {
      generatePreview();
    }
  }, [messageTemplate, packIds, recipients]);

  useEffect(() => {
    if (activeTab === 'tracking' && announcementId) {
      loadStats();
    }
  }, [activeTab, announcementId]);

  const generatePreview = async () => {
    try {
      const token = secureStorage.getItem('nexus_access_token');
      
      // Use first recipient for preview
      const sampleRecipient = recipients[0] || {
        phone: '+60133705567',
        name: 'Sample User',
        first_name: 'Sample'
      };

      const response = await axios.post(
        `${BACKEND_URL}/api/v3/broadcast/deeplink`,
        {
          message_template: messageTemplate,
          pack_ids: packIds || [],
          recipient: sampleRecipient
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setGeneratedMessage(response.data.message);
      setDeepLinks({
        wa_me: response.data.wa_link,
        whatsapp: response.data.wa_direct,
        short_link: response.data.short_link,
        length: response.data.message_length
      });
    } catch (err) {
      console.error('Error generating preview:', err);
    }
  };

  const loadStats = async () => {
    try {
      const token = secureStorage.getItem('nexus_access_token');
      
      const [statusRes, clicksRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/v3/broadcast/${announcementId}/status`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/v3/broadcast/${announcementId}/clicks`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setStats({
        status: statusRes.data,
        clicks: clicksRes.data
      });
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const copyMessage = () => {
    const fallback = (text) => {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      } catch (e) { /* ignore */ }
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(generatedMessage).catch(() => fallback(generatedMessage));
    } else {
      fallback(generatedMessage);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const openWhatsApp = () => {
    // Try WhatsApp app first, fallback to wa.me
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    if (isMobile && deepLinks.whatsapp) {
      window.location.href = deepLinks.whatsapp;
      
      // Fallback to wa.me after 1 second if app doesn't open
      setTimeout(() => {
        window.open(deepLinks.wa_me, '_blank');
      }, 1000);
    } else {
      window.open(deepLinks.wa_me, '_blank');
    }
    
    setShowHelper(true);
  };

  const downloadVCF = async () => {
    try {
      setLoading(true);
      const token = secureStorage.getItem('nexus_access_token');
      
      const response = await axios.post(
        `${BACKEND_URL}/api/v3/broadcast/vcf`,
        { announcement_id: announcementId },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Download file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `broadcast_contacts_${announcementId.substring(0, 8)}.vcf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      alert('✅ Contacts exported! Import to your phone to ensure they can receive broadcasts.');
    } catch (err) {
      alert('😅 Could not export contacts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const markAsSent = async () => {
    if (!window.confirm('Mark this announcement as sent? This action cannot be undone.')) return;
    
    try {
      setLoading(true);
      const token = secureStorage.getItem('nexus_access_token');
      const notes = window.prompt('Add notes (optional):');
      
      await axios.post(
        `${BACKEND_URL}/api/v3/broadcast/${announcementId}/mark-sent`,
        { notes: notes || '' },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      alert('✅ Marked as sent!');
      
      // Reload stats
      if (activeTab === 'tracking') {
        await loadStats();
      }
      
      // Notify parent component
      if (onComplete) onComplete();
    } catch (err) {
      alert('😅 Could not update status.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
      {/* WhatsApp Broadcast Mode Banner */}
      <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border-2 border-green-300">
        <div className="flex items-start gap-3">
          <span className="text-2xl flex-shrink-0">✅</span>
          <div>
            <h4 className="text-sm font-black text-green-900 mb-1">
              WhatsApp Broadcast Mode (ToS-Safe)
            </h4>
            <p className="text-xs text-green-800 leading-relaxed">
              No automation - you'll send manually using WhatsApp's native Broadcast feature. 
              100% compliant with WhatsApp Terms of Service.
            </p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 overflow-x-auto">
        <button
          onClick={() => setActiveTab('compose')}
          className={`px-4 py-2.5 rounded-xl font-bold text-sm whitespace-nowrap transition-all ${
            activeTab === 'compose'
              ? 'bg-blue-600 text-white shadow-lg'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          style={{ minHeight: '44px' }}
        >
          📱 Compose
        </button>
        <button
          onClick={() => setActiveTab('send')}
          className={`px-4 py-2.5 rounded-xl font-bold text-sm whitespace-nowrap transition-all ${
            activeTab === 'send'
              ? 'bg-blue-600 text-white shadow-lg'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          style={{ minHeight: '44px' }}
        >
          📤 Send Helper
        </button>
        <button
          onClick={() => setActiveTab('tracking')}
          className={`px-4 py-2.5 rounded-xl font-bold text-sm whitespace-nowrap transition-all ${
            activeTab === 'tracking'
              ? 'bg-blue-600 text-white shadow-lg'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          style={{ minHeight: '44px' }}
        >
          📊 Tracking
        </button>
      </div>

      {/* A) COMPOSE STEP */}
      {activeTab === 'compose' && (
        <div className="space-y-4">
          {/* Message Preview */}
          <div className="p-4 bg-white rounded-xl border-2 border-gray-200 shadow-sm">
            <h4 className="text-sm font-bold text-gray-900 mb-2">📱 Message Preview</h4>
            <div className="p-3 bg-gray-50 rounded-lg mb-3 font-mono text-sm whitespace-pre-wrap max-h-64 overflow-y-auto">
              {generatedMessage || messageTemplate}
            </div>
            <div className="text-xs text-gray-600 flex justify-between items-center">
              <span>Length: {deepLinks.length || messageTemplate?.length || 0} characters</span>
              <span className={deepLinks.length > 4000 ? 'text-red-600 font-bold' : 'text-green-600'}>
                {deepLinks.length > 4000 ? '⚠️ Too long' : '✅ Good'}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-1 gap-2">
            <button
              onClick={copyMessage}
              disabled={!generatedMessage}
              className="w-full px-4 py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ minHeight: '44px' }}
            >
              {copied ? '✅ Copied!' : '📋 Copy Message'}
            </button>

            <button
              onClick={openWhatsApp}
              disabled={!generatedMessage}
              className="w-full px-4 py-3 bg-green-600 text-white font-bold rounded-xl hover:bg-green-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ minHeight: '44px' }}
            >
              💬 Open WhatsApp
            </button>

            <button
              onClick={downloadVCF}
              disabled={loading || !announcementId}
              className="w-full px-4 py-3 bg-gray-600 text-white font-bold rounded-xl hover:bg-gray-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ minHeight: '44px' }}
            >
              {loading ? '⏳ Downloading...' : '📥 Download Contacts (VCF)'}
            </button>
          </div>
        </div>
      )}

      {/* B) SEND HELPER STEP */}
      {activeTab === 'send' && (
        <div className="space-y-4">
          {/* 3-Step Checklist */}
          <div className="p-4 bg-white rounded-xl border-2 border-gray-200 shadow-sm">
            <h4 className="text-sm font-bold text-gray-900 mb-3">📱 How to Send Broadcast</h4>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0 text-lg">1</div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900">Open WhatsApp</p>
                  <p className="text-xs text-gray-600 mt-1">Message should be pre-filled if you used "Open WhatsApp" button</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0 text-lg">2</div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900">Create New Broadcast</p>
                  <p className="text-xs text-gray-600 mt-1">Menu → New Broadcast List → Select contacts</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0 text-lg">3</div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900">Paste & Send</p>
                  <p className="text-xs text-gray-600 mt-1">Paste the copied message and tap send</p>
                </div>
              </div>
            </div>
          </div>

          {/* Recipients Saved Your Number? Toggle */}
          <div className="p-4 bg-yellow-50 rounded-xl border-2 border-yellow-300">
            <div className="flex items-start gap-3 mb-3">
              <span className="text-xl">⚠️</span>
              <div>
                <h4 className="text-sm font-bold text-yellow-900 mb-1">Important: Recipients Must Save Your Number</h4>
                <p className="text-xs text-yellow-800 leading-relaxed">
                  WhatsApp broadcasts only work if recipients have saved your number in their contacts.
                </p>
              </div>
            </div>
            
            <label className="flex items-center gap-3 p-3 bg-white rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={recipientsSaved}
                onChange={(e) => setRecipientsSaved(e.target.checked)}
                className="w-5 h-5"
              />
              <span className="text-sm font-medium text-gray-900">
                Recipients have saved my number
              </span>
            </label>
          </div>

          {/* Broadcast Limits Warning */}
          <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
            <h4 className="text-xs font-bold text-blue-900 mb-2">📋 Broadcast Limits</h4>
            <ul className="text-xs text-blue-800 space-y-1">
              <li>• Max 256 recipients per broadcast list</li>
              <li>• Messages go to individual chats, not groups</li>
              <li>• Recipients must have your number saved</li>
              <li>• No way to see who received/read the message</li>
            </ul>
          </div>

          {/* Mark as Sent Button */}
          <button
            onClick={markAsSent}
            disabled={loading || !recipientsSaved}
            className="w-full px-4 py-3 bg-green-600 text-white font-bold rounded-xl hover:bg-green-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ minHeight: '44px' }}
          >
            {loading ? '⏳ Saving...' : '✅ I Sent It - Mark as Complete'}
          </button>
        </div>
      )}

      {/* C) TRACKING STEP */}
      {activeTab === 'tracking' && (
        <div className="space-y-4">
          {/* Recipient Count */}
          <div className="p-4 bg-white rounded-xl border-2 border-gray-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-xs font-medium text-gray-600">Total Recipients</h4>
                <p className="text-2xl font-black text-gray-900">{recipients?.length || 0}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <span className="text-2xl">👥</span>
              </div>
            </div>
          </div>

          {/* Click Stats */}
          <div className="p-4 bg-white rounded-xl border-2 border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="text-xs font-medium text-gray-600">Short Link Clicks</h4>
                <p className="text-2xl font-black text-gray-900">
                  {stats?.clicks?.total_clicks || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <span className="text-2xl">🔗</span>
              </div>
            </div>
            
            {stats?.clicks?.pack_stats && stats.clicks.pack_stats.length > 0 && (
              <div className="space-y-2 mt-3 pt-3 border-t border-gray-200">
                {stats.clicks.pack_stats.map((pack, idx) => (
                  <div key={`item-${idx}`} className="flex justify-between items-center text-xs">
                    <span className="text-gray-700 font-medium">{pack.pack_name}</span>
                    <span className="text-gray-900 font-bold">{pack.clicks} clicks</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Send Status */}
          {stats?.status && (
            <div className={`p-4 rounded-xl border-2 ${
              stats.status.status === 'manually_sent'
                ? 'bg-green-50 border-green-300'
                : 'bg-gray-50 border-gray-300'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">
                  {stats.status.status === 'manually_sent' ? '✅' : '⏳'}
                </span>
                <h4 className="text-sm font-bold text-gray-900">
                  {stats.status.status === 'manually_sent' ? 'Marked as Sent' : 'Not Yet Sent'}
                </h4>
              </div>
              
              {stats.status.sent_at && (
                <div className="text-xs text-gray-700 space-y-1">
                  <p><span className="font-semibold">Sent:</span> {new Date(stats.status.sent_at).toLocaleString()}</p>
                  {stats.status.notes && (
                    <p><span className="font-semibold">Notes:</span> {stats.status.notes}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Notes Textarea */}
          <div className="p-4 bg-white rounded-xl border-2 border-gray-200 shadow-sm">
            <h4 className="text-sm font-bold text-gray-900 mb-2">📝 Notes</h4>
            <textarea
              rows={3}
              placeholder="Add notes about this broadcast..."
              className="w-full p-3 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              defaultValue={stats?.status?.notes || ''}
              disabled={stats?.status?.status === 'manually_sent'}
            />
          </div>

          {/* Refresh Stats Button */}
          <button
            onClick={loadStats}
            disabled={loading}
            className="w-full px-4 py-3 bg-gray-600 text-white font-bold rounded-xl hover:bg-gray-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ minHeight: '44px' }}
          >
            {loading ? '⏳ Loading...' : '🔄 Refresh Stats'}
          </button>
        </div>
      )}

      {/* Helper Modal (shown when "Open WhatsApp" is clicked) */}
      {showHelper && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h4 className="text-lg font-bold mb-3">📱 Quick Send Guide</h4>
            
            <div className="space-y-3 mb-4">
              <p className="text-sm text-gray-700">
                WhatsApp should have opened with your message pre-filled. If not, copy the message and paste it manually.
              </p>
              
              <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-xs font-bold text-yellow-900 mb-1">⚠️ Remember</p>
                <ul className="text-xs text-yellow-800 space-y-1">
                  <li>• Create a new Broadcast List (not a group)</li>
                  <li>• Add up to 256 contacts</li>
                  <li>• Only contacts who saved your number will receive</li>
                </ul>
              </div>
            </div>

            <button
              onClick={() => setShowHelper(false)}
              className="w-full px-4 py-2.5 bg-blue-600 text-white font-semibold rounded-lg"
              style={{ minHeight: '44px' }}
            >
              Got It
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BroadcastModeHelper;
